import { legacyCreateColumnHelper as createColumnHelper } from '@tanstack/react-table/legacy'
import { useMemo } from 'react'

import { DataTable } from '@/components/data-table/DataTable'
import { StatCard } from '@/components/stat/StatCard'
import { Badge } from '@/components/ui/badge'
import { useCubeLiveQuery } from '@/hooks/use-cube-live-query'
import { useTablePreferences } from '@/hooks/use-table-preferences'
import { formatInteger, formatText } from '@/lib/format'
import { isInconsistentDeactivation } from '@/lib/hr'
import { messages } from '@/lib/messages'
import { queryKeys } from '@/lib/query-keys'
import {
  HR_VIEWS,
  PERSONEL_MEMBERS,
  hrBreakdownQuery,
  mapBreakdown,
  type PersonelRow,
} from '@/services/cube/hr'
import type { HrAction } from '@/services/http/hr'
import { HrFilterBar } from '@/features/hr/list'
import { useHrCubeFilters, useHrFilterState, useHrList } from '@/features/hr/use-hr-list'
import {
  ActionStatusBadge,
  DetailGrid,
  PersonCell,
  RecordActions,
  RecordHistory,
  SubRowLayout,
  type ActionSpec,
} from '@/features/hr/parts'
import { useLatestActions, useRunAction } from '@/features/hr/hr-actions'
import { HrPageHeader } from '@/features/hr/HrPageHeader'
import { useAuthStore } from '@/stores/auth-store'

const t = messages.hr
const p = t.personnel
const VIEW = HR_VIEWS.personel
const MEASURES = ['count', 'aktifSayisi', 'pasifSayisi', 'passgageHataSayisi']
const column = createColumnHelper<PersonelRow>()

function DiaBadge({ durum }: { durum: string | null }) {
  if (!durum) return null
  return (
    <Badge tone={durum === 'A' ? 'success' : 'neutral'} size="sm">
      {t.diaStatus[durum] ?? durum}
    </Badge>
  )
}

function PassgageBadge({ row }: { row: PersonelRow }) {
  const tone =
    row.passgageDurum === 'aktarildi'
      ? 'success'
      : row.passgageDurum === 'hatali'
        ? 'danger'
        : 'warning'
  return (
    <span className="flex flex-nowrap gap-1 overflow-hidden">
      <Badge tone={tone} size="sm">
        {t.passgagePush[row.passgageDurum] ?? row.passgageDurum}
      </Badge>
      {row.passgageAktif !== null && (
        <Badge tone={row.passgageAktif ? 'success' : 'neutral'} size="sm">
          {t.passgageActive[String(row.passgageAktif)]}
        </Badge>
      )}
    </span>
  )
}

function CompleteDeactivation({ row }: { row: PersonelRow }) {
  const run = useRunAction('personel', row.sicilno)
  const allowed = useAuthStore((state) => state.hasPermission('hr.personnel:deactivate'))
  if (!allowed) return null
  return (
    <button
      type="button"
      className="text-brand text-xs underline-offset-2 hover:underline"
      disabled={run.isPending}
      onClick={(event) => {
        event.stopPropagation()
        run.mutate({ type: 'personel_pasife_al_tamamla', params: { firma_tablo: row.firmaTablo } })
      }}
    >
      {t.actions.complete}
    </button>
  )
}

function PersonnelDetail({ row, action }: { row: PersonelRow; action: HrAction | undefined }) {
  const params = { firma_tablo: row.firmaTablo }
  const actions: ActionSpec[] = [
    { type: 'personel_dia_guncel_al', params },
    { type: 'personel_passgage_tekrar_gonder', params },
  ]
  const deactivate: ActionSpec[] = []
  if (isInconsistentDeactivation(action)) {
    deactivate.push({
      type: 'personel_pasife_al_tamamla',
      params,
      permission: 'hr.personnel:deactivate',
    })
  } else if (row.diaDurum === 'A') {
    deactivate.push({
      type: 'personel_pasife_al',
      params,
      danger: true,
      permission: 'hr.personnel:deactivate',
      confirm: { title: t.actions.deactivateTitle, body: t.actions.deactivateBody },
    })
  }
  return (
    <SubRowLayout
      detail={
        <>
          <DetailGrid
            items={[
              [t.detail.fields.sicil, row.sicilno],
              [t.detail.fields.firma, formatText(row.firma)],
              [t.detail.fields.sube, formatText(row.sube)],
              [t.detail.fields.pozisyon, formatText(row.pozisyon)],
              [t.detail.fields.iseBaslama, formatText(row.iseBaslama)],
              [t.filters.diaStatus, <DiaBadge key="dia" durum={row.diaDurum} />],
              [t.filters.passgageStatus, <PassgageBadge key="pg" row={row} />],
              [t.detail.fields.passgageId, formatText(row.passgageId)],
              [t.detail.error, formatText(row.sonHata)],
            ]}
          />
          {isInconsistentDeactivation(action) && (
            <p className="text-warning text-xs">{t.actions.inconsistentHint}</p>
          )}
          <div className="flex flex-wrap gap-2">
            <RecordActions kind="personel" id={row.sicilno} actions={[...actions, ...deactivate]} />
          </div>
        </>
      }
      history={<RecordHistory kind="personel" id={row.sicilno} />}
    />
  )
}

/** Şube başına aktif/toplam personel (filtrelere göre). */
function BranchBreakdown({ filters }: { filters: ReturnType<typeof useHrCubeFilters> }) {
  const state = useHrFilterState()
  const query = useCubeLiveQuery({
    queryKey: queryKeys.cube(VIEW, { kind: 'branch-stats', filters }),
    query: hrBreakdownQuery(VIEW, ['firma', 'sube'], ['count', 'aktifSayisi'], filters),
    select: mapBreakdown(VIEW, ['firma', 'sube'], ['count', 'aktifSayisi']),
    subscribe: false,
  })
  const rows = query.data ?? []
  if (rows.length === 0) return null
  return (
    <section aria-label={p.branches} className="flex flex-col gap-2">
      <h2 className="text-text-secondary text-xs font-medium">{p.branches}</h2>
      <div className="flex flex-wrap gap-2">
        {rows.map((row) => {
          const selected = !!row.sube && state.sube.includes(row.sube)
          return (
            <button
              key={`${row.firma}-${row.sube}`}
              type="button"
              aria-pressed={selected}
              disabled={!row.sube}
              onClick={() =>
                row.sube &&
                state.query.set({
                  sube: selected
                    ? state.sube.filter((value) => value !== row.sube)
                    : [...state.sube, row.sube],
                })
              }
              className="border-border bg-surface hover:bg-surface-muted aria-pressed:border-brand rounded-control flex items-baseline gap-2 border px-2.5 py-1.5 text-left text-xs transition-colors"
            >
              <span className="flex flex-col">
                <span className="font-medium">{row.sube ?? t.unknown}</span>
                <span className="text-text-muted">{row.firma ?? t.unknown}</span>
              </span>
              <span className="tabular-nums">
                {formatInteger(row.aktifSayisi)}
                <span className="text-text-muted">/{formatInteger(row.count)}</span>
              </span>
            </button>
          )
        })}
      </div>
    </section>
  )
}

export function PersonnelPage() {
  const state = useHrFilterState()
  const filters = useHrCubeFilters(VIEW, {
    statusMember: 'diaDurum',
    categoryMember: 'passgageDurum',
  })
  const list = useHrList<PersonelRow>({
    view: VIEW,
    members: PERSONEL_MEMBERS,
    rowKey: 'personelKey',
    filters,
    defaultSort: [{ id: 'adisoyadi', desc: false }],
    measures: MEASURES,
  })
  const actions = useLatestActions(
    'personel',
    list.rows.map((row) => row.sicilno),
    VIEW,
  )
  const preferences = useTablePreferences('ikpersonel', {})

  const columns = useMemo(
    () => [
      column.accessor((row) => row.adisoyadi, {
        id: 'adisoyadi',
        header: p.columns.ad,
        size: 220,
        cell: (info) => <PersonCell name={info.row.original.adisoyadi} />,
      }),
      column.accessor((row) => row.sicilno, { id: 'sicilno', header: p.columns.sicil, size: 90 }),
      column.accessor((row) => row.firma, {
        id: 'firma',
        header: p.columns.firma,
        cell: (info) => formatText(info.getValue()),
      }),
      column.accessor((row) => row.sube, {
        id: 'sube',
        header: p.columns.sube,
        cell: (info) => formatText(info.getValue()),
      }),
      column.accessor((row) => row.pozisyon, {
        id: 'pozisyon',
        header: p.columns.pozisyon,
        cell: (info) => formatText(info.getValue()),
      }),
      column.accessor((row) => row.diaDurum, {
        id: 'diaDurum',
        header: p.columns.dia,
        size: 100,
        cell: (info) => <DiaBadge durum={info.getValue()} />,
        meta: { exportValue: (row: PersonelRow) => t.diaStatus[row.diaDurum ?? ''] ?? '' },
      }),
      column.accessor((row) => row.passgageDurum, {
        id: 'passgageDurum',
        header: p.columns.passgage,
        size: 230,
        cell: (info) => <PassgageBadge row={info.row.original} />,
        meta: { exportValue: (row: PersonelRow) => t.passgagePush[row.passgageDurum] ?? '' },
      }),
      column.accessor((row) => row.sonHata, {
        id: 'sonHata',
        header: p.columns.hata,
        cell: (info) => formatText(info.getValue()),
      }),
      column.display({
        id: 'aksiyon',
        header: p.columns.aksiyon,
        size: 150,
        enableSorting: false,
        cell: (info) => {
          const action = actions.get(info.row.original.sicilno)
          return (
            <span className="flex items-center gap-2">
              <ActionStatusBadge action={action} />
              {isInconsistentDeactivation(action) && (
                <CompleteDeactivation row={info.row.original} />
              )}
            </span>
          )
        },
        meta: { exportable: false },
      }),
    ],
    [actions],
  )

  const m = list.measures
  return (
    <div className="flex flex-col gap-4">
      <HrPageHeader title={p.title} description={p.description} />
      <HrFilterBar
        view={VIEW}
        idPrefix="hr-personel"
        optionFilters={[
          {
            key: 'durum',
            label: t.filters.diaStatus,
            options: Object.entries(t.diaStatus).map(([value, label]) => ({ value, label })),
          },
          {
            key: 'kategori',
            label: t.filters.passgageStatus,
            options: Object.entries(t.passgagePush).map(([value, label]) => ({ value, label })),
          },
        ]}
      />
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard label={p.total} value={formatInteger(m.count)} busy={list.busy} />
        <StatCard label={p.active} value={formatInteger(m.aktifSayisi)} busy={list.busy} />
        <StatCard label={p.passive} value={formatInteger(m.pasifSayisi)} busy={list.busy} />
        <StatCard
          label={p.passgageErrors}
          value={formatInteger(m.passgageHataSayisi)}
          busy={list.busy}
        />
      </div>
      <BranchBreakdown filters={filters} />
      <DataTable
        key={state.firma.join()}
        tableId="ikpersonel"
        title={p.title}
        columns={columns}
        data={list.rows}
        total={list.total}
        getRowId={(row) => row.personelKey}
        sorting={list.sorting}
        onSortingChange={list.onSortingChange}
        pagination={list.pagination}
        onPaginationChange={list.setPagination}
        isLoading={list.isLoading}
        busy={list.busy}
        isError={list.isError}
        error={list.error}
        onRetry={list.refetch}
        layout={preferences.layout}
        onLayoutChange={preferences.save}
        onLayoutReset={preferences.reset}
        renderSubRow={(row) => <PersonnelDetail row={row} action={actions.get(row.sicilno)} />}
      />
    </div>
  )
}
